import { useMutation } from '@tanstack/vue-query'

import type { ApiResponse, Team, User } from '@/types/user'

import { useAxios } from '@/composables/use-axios'

/**
 * API配置 - 基于老项目的URL配置
 */

// API完整域名配置
const API_BASE_URL_V2_1 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-1/`
const API_BASE_URL_V1_3 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v1-3/`
const API_BASE_URL_V2_2 = `${import.meta.env.VITE_SERVER_API_PLATFORM}/v2-2/`
const API_DATA_URL_V2_2 = `${import.meta.env.VITE_SERVER_API_DATA}/v2-2/`

/**
 * 团队相关API类型定义
 */

// 团队切换请求数据
interface TeamSwitchRequest {
  team_id: number
}

// 团队切换响应数据
interface TeamSwitchResponse {
  token: string
  user: User
}

// 团队初始化响应数据 - 继承User并添加额外字段
export interface TeamInitAppResponse extends User {
  // API 响应中可能包含的额外字段
  bill_sub_items?: {
    default_model?: string
    access_flag?: boolean
    free_limit_track_revenue?: number
    plan?: {
      item_plan: string
      item_plan_type?: string
      item_billing_plan_type?: string
    }
  }
  shopify_server_time?: {
    timestamp: number
    time_bj: string
    time_utc: string
  }
  question?: {
    need_answer: boolean
    not_skip_type: string[]
  }
  question_complete?: {
    now_page_info?: string
    is_finished: boolean
    question_finished: boolean
  }
  is_version_4?: boolean
  bill_access?: {
    team_id: number
    is_pay: boolean
    pay_type: string
    free_time: string
    access_flag: boolean
    auth_limit_flag: boolean
    trial_days: number
    billing_type: string
    residual_trial_days: number
    next_pay_time: string
    has_first_month_discount: boolean
    total_nums: string
    use_nums: string
    volume_end_time: string
    waring_type: number
    date: string
    inner_team: boolean
  }
  team_auth?: {
    auth_map: {
      [key: string]: {
        available?: string
        limited?: boolean
        allowed_date_range?: number
        allowed_day_range?: {
          from: number
          to: number
        }
        allow_types?: string[]
        limited_number?: number
        add_on?: boolean
      }
    }
    review_completed?: boolean
  }
}

// Conversion Window 响应数据
interface ConversionWindowResponse {
  conversion_window: number
}

// Theme 状态响应数据（用于检测 app embed 是否已启用）
export interface ThemeStatusResponse {
  code: number
  theme_config: any
}

// 检查 App Embed 是否已启用（通过 init 接口返回的 theme.code）
// theme.code === 4 表示已启用
export async function checkAppEmbedStatusApi(): Promise<{ enabled: boolean, themeCode: number }> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<{ code: number, message: string, data: { theme?: ThemeStatusResponse } }>(`${API_BASE_URL_V2_1}attribuly/init`)
  const theme = response.data?.data?.theme
  const themeCode = theme?.code ?? 0
  return { enabled: themeCode === 4, themeCode }
}

// 检查 App Embed 状态 - Vue Query Hook 版本
export function useCheckAppEmbedStatus() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['checkAppEmbedStatus'],
    mutationFn: async (): Promise<{ enabled: boolean, themeCode: number }> => {
      const response = await axiosInstance.post<{ code: number, message: string, data: { theme?: ThemeStatusResponse } }>(`${API_BASE_URL_V2_1}attribuly/init`)
      const theme = response.data?.data?.theme
      const themeCode = theme?.code ?? 0
      return { enabled: themeCode === 4, themeCode }
    },
  })
}

// 任务相关响应数据
interface OnboardingTaskCompleteInfo {
  task_id: number
  is_finished: boolean
  type?: string
}

interface TeamBillItemsResponse {
  plan?: {
    event_charge?: {
      recurring_quota?: number
      extra_quota?: number
      usage?: number
    }
  }
}

interface AanFeatureListResponse {
  aan_status: string
  max_emails: string
  trigger_rules: string
  vvjs: string
}

// 任务完成请求数据
interface OnboardingTaskCompleteRequest {
  task_id: number
}

/**
 * 团队相关API
 */

// 团队切换 - 普通函数版本（用于 Pinia store）
export async function teamSwitchApi(data: TeamSwitchRequest): Promise<TeamSwitchResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<TeamSwitchResponse>>(`${API_BASE_URL_V1_3}team/switch`, data)
  return response.data?.data
}

// 团队切换 - Vue Query Hook 版本（用于组件）
export function useTeamSwitch() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['teamSwitch'],
    mutationFn: async (data: TeamSwitchRequest): Promise<TeamSwitchResponse> => {
      const response = await axiosInstance.post<ApiResponse<TeamSwitchResponse>>(`${API_BASE_URL_V1_3}team/switch`, data)
      return response.data?.data
    },
  })
}

// 团队初始化应用
export function useTeamInitApp() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['teamInitApp'],
    mutationFn: async (): Promise<TeamInitAppResponse> => {
      // 先执行原始的初始化
      const initResponse = await axiosInstance.post<ApiResponse<TeamInitAppResponse>>(`${API_BASE_URL_V2_1}attribuly/init`)
      const initData = initResponse.data?.data

      // 如果初始化成功且有team信息，获取conversion window
      if (initData && initData.team) {
        try {
          const conversionWindowResponse = await axiosInstance.post<ApiResponse<ConversionWindowResponse>>(`${API_DATA_URL_V2_2}setting/conversion/window/info`)
          const conversionWindowData = conversionWindowResponse.data?.data

          // 将conversion_window添加到team对象中
          if (conversionWindowData && conversionWindowData.conversion_window !== undefined) {
            initData.team.conversion_window = conversionWindowData.conversion_window

            // 如果team_list存在，也更新对应team的conversion_window
            if (initData.team_list && Array.isArray(initData.team_list)) {
              initData.team_list = initData.team_list.map((team: Team) => {
                if (team.id === initData.team.id) {
                  return { ...team, conversion_window: conversionWindowData.conversion_window }
                }
                return team
              })
            }
          }
        }
        catch (error) {
          console.warn('Failed to fetch conversion window:', error)
          // 如果获取conversion window失败，设置默认值
          initData.team.conversion_window = 7
          if (initData.team_list && Array.isArray(initData.team_list)) {
            initData.team_list = initData.team_list.map((team: Team) => {
              if (team.id === initData.team.id) {
                return { ...team, conversion_window: 7 }
              }
              return team
            })
          }
        }
      }

      return initData
    },
  })
}

// 获取当前归因模型（conversion window）- 独立API
export function useCurrentAttributionModel() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['currentAttributionModel'],
    mutationFn: async (): Promise<ConversionWindowResponse> => {
      const response = await axiosInstance.post<ApiResponse<ConversionWindowResponse>>(`${API_DATA_URL_V2_2}setting/conversion/window/info`)
      return response.data?.data
    },
  })
}

/**
 * 任务相关API
 */

// 获取任务完成信息 - Vue Query Hook 版本
export function useOnboardingTaskCompleteInfo() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['onboardingTaskCompleteInfo'],
    mutationFn: async (): Promise<OnboardingTaskCompleteInfo[]> => {
      const response = await axiosInstance.post<ApiResponse<OnboardingTaskCompleteInfo[]>>(`${API_BASE_URL_V2_1}v6-onboarding-task/complete/info`)
      return response.data?.data || []
    },
  })
}

// 完成任务 - Vue Query Hook 版本
export function useOnboardingTaskComplete() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['onboardingTaskComplete'],
    mutationFn: async (data: OnboardingTaskCompleteRequest): Promise<any> => {
      const response = await axiosInstance.post<ApiResponse<any>>(`${API_BASE_URL_V2_1}v6-onboarding-task/complete`, data)
      return response.data?.data
    },
  })
}

// 获取团队账单信息 - Vue Query Hook 版本
export function useTeamBillItems() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['teamBillItems'],
    mutationFn: async (): Promise<TeamBillItemsResponse> => {
      const response = await axiosInstance.post<ApiResponse<TeamBillItemsResponse>>(`${API_BASE_URL_V2_1}attribuly/bill-items`)
      return response.data?.data || {}
    },
  })
}

// 获取任务完成信息 - 普通函数版本（用于 Pinia store）
export async function onboardingTaskCompleteInfoApi(): Promise<OnboardingTaskCompleteInfo[]> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<OnboardingTaskCompleteInfo[]>>(`${API_BASE_URL_V2_1}v6-onboarding-task/complete/info`)
  return response.data?.data || []
}

// 完成任务 - 普通函数版本（用于 Pinia store）
export async function onboardingTaskCompleteApi(data: OnboardingTaskCompleteRequest): Promise<any> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<any>>(`${API_BASE_URL_V2_1}v6-onboarding-task/complete`, data)
  return response.data?.data
}

// 获取团队账单信息 - 普通函数版本（用于 Pinia store）
export async function teamBillItemsApi(): Promise<TeamBillItemsResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<TeamBillItemsResponse>>(`${API_BASE_URL_V2_1}attribuly/bill-items`)
  return response.data?.data || {}
}

// 权限规则响应类型
interface AuthRuleResponse {
  auth_map: {
    [key: string]: {
      available?: string
      limited?: boolean
      allowed_date_range?: number
      allowed_day_range?: {
        from: number
        to: number
      }
      allow_types?: string[]
      limited_number?: number
      add_on?: boolean
    }
  }
}

// 获取团队权限规则 - 普通函数版本（用于 Pinia store）
export async function teamAuthRuleApi(): Promise<ApiResponse<AuthRuleResponse>> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<AuthRuleResponse>>(`${API_BASE_URL_V2_2}team/auth-rule`)
  return response.data
}

/**
 * Self-site 登录或注册相关API
 */

// Self-site 登录或注册请求数据
export interface SelfSiteLoginOrRegisterRequest {
  domain: string
  shop_name: string
  currency: string
  timezone: string
  email: string
  user_id: string
  shop_type: 'self'
}

// Self-site 登录或注册响应数据
export interface SelfSiteLoginOrRegisterResponse {
  user: {
    id: number
    name: string
    team_id: number
    hash: string
  }
  teams: Array<{
    id: number
    name: string
    web_url: string
    team_code: string
    currency?: string
    timezone?: string
  }>
  token: string
  expires_in: number
}

// Self-site 登录或注册 - 普通函数版本（用于 Pinia store）
export async function selfSiteLoginOrRegisterApi(data: SelfSiteLoginOrRegisterRequest): Promise<SelfSiteLoginOrRegisterResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<SelfSiteLoginOrRegisterResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/self/login-or-register`, data)
  return response.data?.data
}

// Self-site 登录或注册 - Vue Query Hook 版本（用于组件）
export function useSelfSiteLoginOrRegister() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['selfSiteLoginOrRegister'],
    mutationFn: async (data: SelfSiteLoginOrRegisterRequest): Promise<SelfSiteLoginOrRegisterResponse> => {
      const response = await axiosInstance.post<ApiResponse<SelfSiteLoginOrRegisterResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/self/login-or-register`, data)
      return response.data?.data
    },
  })
}

// 检查 WebPixel 安装状态
export function useCheckWebPixel() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['checkWebPixel'],
    mutationFn: async (): Promise<{ code: number, message: string, data: { install_sign: boolean } }> => {
      const response = await axiosInstance.post<{ code: number, message: string, data: { install_sign: boolean } }>(`${API_BASE_URL_V2_1}check-webpixel`)
      return response.data
    },
  })
}

// 设置 WebPixel 安装时间
export function useSetWebPixelTime() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['setWebPixelTime'],
    mutationFn: async (): Promise<{ code: number, message: string, data?: { webpixel_time: string } }> => {
      const response = await axiosInstance.post(`${API_BASE_URL_V2_1}set-webpixel-time`)
      return response.data
    },
  })
}

export interface UpdateSelfTeamRequest {
  domain: string
  shop: string
  currency: string
  timezone: string
}

export function useUpdateSelfTeam() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['updateSelfTeam'],
    mutationFn: async (data: UpdateSelfTeamRequest): Promise<{ code: number, message: string, data?: null }> => {
      const response = await axiosInstance.post(`${API_BASE_URL_V2_1}update-self-team`, data)
      return response.data
    },
  })
}

// 获取 AAN 功能配置
export function useAanFeatureList() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['aanFeatureList'],
    mutationFn: async (): Promise<AanFeatureListResponse | undefined> => {
      const response = await axiosInstance.post<ApiResponse<AanFeatureListResponse>>(`${API_BASE_URL_V2_1}frontend/config/get_list`)
      return response.data?.data
    },
  })
}

// 获取 AAN 功能配置 - 普通函数版本
export async function aanFeatureListApi(): Promise<AanFeatureListResponse | undefined> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<AanFeatureListResponse>>(`${API_BASE_URL_V2_1}frontend/config/get_list`)
  return response.data?.data
}

/**
 * WooCommerce 登录或注册相关API
 */

// WooCommerce 登录或注册请求数据
export interface WooCommerceLoginOrRegisterRequest {
  domain: string
  key: string
  secret: string
  user_id: number
  email: string
}

// WooCommerce 登录或注册响应数据（与 SelfSite 返回结构一致）
export interface WooCommerceLoginOrRegisterResponse {
  user: {
    id: number
    name: string
    team_id: number
    hash: string
  }
  teams: Array<{
    id: number
    name: string
    web_url: string
    team_code: string
  }>
  token: string
  expires_in: number
}

// WooCommerce 登录或注册 - 普通函数版本（用于 Pinia store）
export async function wooCommerceLoginOrRegisterApi(data: WooCommerceLoginOrRegisterRequest): Promise<WooCommerceLoginOrRegisterResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<WooCommerceLoginOrRegisterResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/woo/login-or-register`, data)
  return response.data?.data
}

// WooCommerce 登录或注册 - Vue Query Hook 版本（用于组件）
export function useWooCommerceLoginOrRegister() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['wooCommerceLoginOrRegister'],
    mutationFn: async (data: WooCommerceLoginOrRegisterRequest): Promise<WooCommerceLoginOrRegisterResponse> => {
      const response = await axiosInstance.post<ApiResponse<WooCommerceLoginOrRegisterResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/woo/login-or-register`, data)
      return response.data?.data
    },
  })
}

// WooCommerce 安装验证请求数据
export interface WooCommerceInstallVerifyRequest {
  domain: string
  key: string
  secret: string
}

// WooCommerce 安装验证响应数据
export interface WooCommerceInstallVerifyResponse {
  install_verify: boolean
}

// WooCommerce 安装验证 - 普通函数版本（用于 Pinia store）
export async function wooCommerceInstallVerifyApi(data: WooCommerceInstallVerifyRequest): Promise<WooCommerceInstallVerifyResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<WooCommerceInstallVerifyResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/woo/install-verify`, data)
  return response.data?.data
}

// WooCommerce 安装验证 - Vue Query Hook 版本（用于组件）
export function useWooCommerceInstallVerify() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['wooCommerceInstallVerify'],
    mutationFn: async (data: WooCommerceInstallVerifyRequest): Promise<WooCommerceInstallVerifyResponse> => {
      const response = await axiosInstance.post<ApiResponse<WooCommerceInstallVerifyResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/woo/install-verify`, data)
      return response.data?.data
    },
  })
}

// WooCommerce 下载安装包请求数据
export interface WooCommerceDownloadCodeRequest {
  domain: string
  key: string
  secret: string
}

// WooCommerce 下载安装包响应数据
export interface WooCommerceDownloadCodeResponse {
  download_url: string
}

// WooCommerce 下载安装包 - 普通函数版本（用于 Pinia store）
export async function wooCommerceDownloadCodeApi(data: WooCommerceDownloadCodeRequest): Promise<WooCommerceDownloadCodeResponse> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.post<ApiResponse<WooCommerceDownloadCodeResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/woo/download-code`, data)
  return response.data?.data
}

// WooCommerce 下载安装包 - Vue Query Hook 版本（用于组件）
export function useWooCommerceDownloadCode() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['wooCommerceDownloadCode'],
    mutationFn: async (data: WooCommerceDownloadCodeRequest): Promise<WooCommerceDownloadCodeResponse> => {
      const response = await axiosInstance.post<ApiResponse<WooCommerceDownloadCodeResponse>>(`${API_BASE_URL_V2_1}attribuly/auth/woo/download-code`, data)
      return response.data?.data
    },
  })
}

/**
 * Members 相关API
 */

// 成员数据类型
export interface MemberData {
  id: number
  user_id: number // 用户ID，用于API调用
  name: string
  email: string
  created_at: string
  status: number // 0 = Pending, 1 = Active
  administer_flag: boolean
  creator_flag?: boolean // 标记是否是创建者（Owner）
  access_pages?: string[]
  access_channel_groups?: string[]
}

// 获取团队成员列表 - 普通函数版本（用于 Pinia store）
export async function googleMembersApi(): Promise<MemberData[]> {
  const { axiosInstance } = useAxios()
  const response = await axiosInstance.get<ApiResponse<MemberData[]>>(`${API_BASE_URL_V2_1}google/members`)
  return response.data?.data || []
}

// 获取团队成员列表 - Vue Query Hook 版本（用于组件）
export function useGoogleMembers() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['googleMembers'],
    mutationFn: async (): Promise<MemberData[]> => {
      const response = await axiosInstance.get<ApiResponse<MemberData[]>>(`${API_BASE_URL_V2_1}google/members`)
      return response.data?.data || []
    },
  })
}

// 删除团队成员请求数据
export interface RemoveMemberRequest {
  id: number
}

// 删除团队成员 - 普通函数版本（用于 Pinia store）
export async function googleRemoveApi(data: RemoveMemberRequest): Promise<void> {
  const { axiosInstance } = useAxios()
  await axiosInstance.post(`${API_BASE_URL_V2_1}google/remove-member`, data)
}

// 删除团队成员 - Vue Query Hook 版本（用于组件）
export function useGoogleRemove() {
  const { axiosInstance } = useAxios()
  return useMutation({
    mutationKey: ['googleRemove'],
    mutationFn: async (data: RemoveMemberRequest): Promise<void> => {
      await axiosInstance.post(`${API_BASE_URL_V2_1}google/remove-member`, data)
    },
  })
}
